import { useState, useCallback } from "react";
import { SNAKES, LADDERS, INCIDENT_CARDS, RANK_THRESHOLDS } from "@/mocks/gameData";
import type { IncidentCard } from "@/mocks/gameData";

export interface GameEvent {
  id: string;
  type: "move" | "snake" | "ladder" | "card" | "win" | "start";
  message: string;
  square?: number;
  timestamp: string;
}

export interface GameState {
  currentSquare: number;
  turn: number;
  events: GameEvent[];
  isRolling: boolean;
  diceValue: number | null;
  gameOver: boolean;
  correctCards: number;
  pendingCard: IncidentCard | null;
  pendingSnake: { head: number; tail: number; label: string; description: string } | null;
  pendingLadder: { bottom: number; top: number; label: string; description: string } | null;
}

function getTimestamp() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
}

function getRank(square: number) {
  for (const t of RANK_THRESHOLDS) {
    if (square >= t.min) return t;
  }
  return RANK_THRESHOLDS[RANK_THRESHOLDS.length - 1];
}

const initialState: GameState = {
  currentSquare: 0,
  turn: 0,
  events: [
    {
      id: "start",
      type: "start",
      message: "Game started. Roll the dice to begin your incident response journey.",
      timestamp: getTimestamp(),
    },
  ],
  isRolling: false,
  diceValue: null,
  gameOver: false,
  correctCards: 0,
  pendingCard: null,
  pendingSnake: null,
  pendingLadder: null,
};

export function useGameState() {
  const [state, setState] = useState<GameState>(initialState);

  const addEvent = useCallback((event: Omit<GameEvent, "id" | "timestamp">) => {
    setState((prev) => ({
      ...prev,
      events: [
        {
          ...event,
          id: `evt-${Date.now()}-${Math.random()}`,
          timestamp: getTimestamp(),
        },
        ...prev.events,
      ].slice(0, 50),
    }));
  }, []);

  const rollDice = useCallback(() => {
    setState((prev) => {
      if (prev.isRolling || prev.gameOver || prev.pendingCard || prev.pendingSnake || prev.pendingLadder) return prev;
      return { ...prev, isRolling: true };
    });

    setTimeout(() => {
      const roll = Math.floor(Math.random() * 6) + 1;

      setState((prev) => {
        if (!prev.isRolling) return prev;

        let newSquare = prev.currentSquare + roll;
        const newTurn = prev.turn + 1;

        if (newSquare > 100) {
          newSquare = 100 - (newSquare - 100);
        }

        const events: Omit<GameEvent, "id" | "timestamp">[] = [
          {
            type: "move",
            message: `Rolled a ${roll}. Moved to square ${newSquare}.`,
            square: newSquare,
          },
        ];

        // Check win
        if (newSquare === 100) {
          return {
            ...prev,
            currentSquare: 100,
            turn: newTurn,
            diceValue: roll,
            isRolling: false,
            gameOver: true,
            events: [
              {
                id: `evt-win`,
                type: "win",
                message: "You reached square 100! Incident resolved. Production Guardian achieved!",
                square: 100,
                timestamp: getTimestamp(),
              },
              ...events.map((e) => ({ ...e, id: `evt-${Date.now()}`, timestamp: getTimestamp() })),
              ...prev.events,
            ].slice(0, 50),
          };
        }

        // Check incident card
        const card = INCIDENT_CARDS.find((c) => c.square === newSquare);
        if (card) {
          return {
            ...prev,
            currentSquare: newSquare,
            turn: newTurn,
            diceValue: roll,
            isRolling: false,
            pendingCard: card,
            events: [
              {
                id: `evt-card-${Date.now()}`,
                type: "card",
                message: `Incident card triggered on square ${newSquare}! Answer the scenario.`,
                square: newSquare,
                timestamp: getTimestamp(),
              },
              ...events.map((e) => ({ ...e, id: `evt-${Date.now()}`, timestamp: getTimestamp() })),
              ...prev.events,
            ].slice(0, 50),
          };
        }

        // Check snake
        const snake = SNAKES.find((s) => s.head === newSquare);
        if (snake) {
          return {
            ...prev,
            currentSquare: newSquare,
            turn: newTurn,
            diceValue: roll,
            isRolling: false,
            pendingSnake: snake,
            events: [
              {
                id: `evt-snake-${Date.now()}`,
                type: "snake",
                message: `Snake! "${snake.label}" — sliding down from ${snake.head} to ${snake.tail}.`,
                square: newSquare,
                timestamp: getTimestamp(),
              },
              ...events.map((e) => ({ ...e, id: `evt-${Date.now()}`, timestamp: getTimestamp() })),
              ...prev.events,
            ].slice(0, 50),
          };
        }

        // Check ladder
        const ladder = LADDERS.find((l) => l.bottom === newSquare);
        if (ladder) {
          return {
            ...prev,
            currentSquare: newSquare,
            turn: newTurn,
            diceValue: roll,
            isRolling: false,
            pendingLadder: ladder,
            events: [
              {
                id: `evt-ladder-${Date.now()}`,
                type: "ladder",
                message: `Ladder! "${ladder.label}" — climbing from ${ladder.bottom} to ${ladder.top}.`,
                square: newSquare,
                timestamp: getTimestamp(),
              },
              ...events.map((e) => ({ ...e, id: `evt-${Date.now()}`, timestamp: getTimestamp() })),
              ...prev.events,
            ].slice(0, 50),
          };
        }

        return {
          ...prev,
          currentSquare: newSquare,
          turn: newTurn,
          diceValue: roll,
          isRolling: false,
          events: [
            ...events.map((e) => ({ ...e, id: `evt-${Date.now()}-${Math.random()}`, timestamp: getTimestamp() })),
            ...prev.events,
          ].slice(0, 50),
        };
      });
    }, 600);
  }, []);

  const resolveSnake = useCallback(() => {
    setState((prev) => {
      if (!prev.pendingSnake) return prev;
      return {
        ...prev,
        currentSquare: prev.pendingSnake.tail,
        pendingSnake: null,
      };
    });
  }, []);

  const resolveLadder = useCallback(() => {
    setState((prev) => {
      if (!prev.pendingLadder) return prev;
      const newSquare = prev.pendingLadder.top;
      if (newSquare === 100) {
        return {
          ...prev,
          currentSquare: 100,
          pendingLadder: null,
          gameOver: true,
        };
      }
      return {
        ...prev,
        currentSquare: newSquare,
        pendingLadder: null,
      };
    });
  }, []);

  const resolveCard = useCallback((correct: boolean) => {
    setState((prev) => {
      if (!prev.pendingCard) return prev;
      const delta = correct ? prev.pendingCard.reward : -prev.pendingCard.penalty;
      let newSquare = Math.max(1, prev.currentSquare + delta);
      newSquare = Math.min(100, newSquare);
      const gameOver = newSquare === 100;
      return {
        ...prev,
        currentSquare: newSquare,
        correctCards: correct ? prev.correctCards + 1 : prev.correctCards,
        pendingCard: null,
        gameOver,
        events: [
          {
            id: `evt-card-res-${Date.now()}`,
            type: "card",
            message: correct
              ? `Correct! +${prev.pendingCard.reward} squares. Now on square ${newSquare}.`
              : `Incorrect. -${prev.pendingCard.penalty} squares. Now on square ${newSquare}.`,
            square: newSquare,
            timestamp: getTimestamp(),
          },
          ...prev.events,
        ].slice(0, 50),
      };
    });
  }, []);

  const restartGame = useCallback(() => {
    setState({
      ...initialState,
      events: [
        {
          id: `start-${Date.now()}`,
          type: "start",
          message: "Game restarted. Roll the dice to begin.",
          timestamp: getTimestamp(),
        },
      ],
    });
  }, []);

  const currentRank = getRank(state.currentSquare);

  return { state, rollDice, resolveCard, resolveSnake, resolveLadder, restartGame, currentRank };
}
